import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppLayout";
import { UpgradeGate } from "@/components/UpgradeGate";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { INDUSTRIES, STATES } from "@/lib/constants";
import { toast } from "sonner";

const TYPES = ["LLC", "Sole Proprietorship", "Corporation", "Nonprofit (501c3)", "Partnership"];

function GuideForm() {
  const navigate = useNavigate();
  const [f, setF] = useState({ state: "", industry: "Small Business", business_type: "LLC", city: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.state) return toast.error("Choose a state");
    setBusy(true);
    try {
      const { data } = await api.post("/licensing", f);
      navigate(`/dashboard/licensing/${data.guide_id}`);
    } catch (e2) {
      toast.error(formatErr(e2));
      setBusy(false);
    }
  };
  const sel = (k, opts, tid, ph) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger className="mt-1.5 h-11" data-testid={tid}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="licensing-form">
      <div className="md:col-span-3"><Label>State</Label>{sel("state", STATES, "licensing-state-select", "Select a state")}</div>
      <div className="md:col-span-3"><Label>Industry</Label>{sel("industry", INDUSTRIES, "licensing-industry-select")}</div>
      <div className="md:col-span-2"><Label>Business type</Label>{sel("business_type", TYPES, "licensing-type-select")}</div>
      <div className="md:col-span-2"><Label>City</Label><Input className="mt-1.5 h-11" placeholder="Optional" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="licensing-city-input" /></div>
      <div className="md:col-span-2 flex items-end"><Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="licensing-submit-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Build guide</Button></div>
    </form>
  );
}

export default function Licensing() {
  const { hasPkg } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const allowed = hasPkg("licensing_guide");
  useEffect(() => { if (allowed) api.get("/licensing").then((r) => setItems(r.data)); }, [allowed]);
  return (
    <div>
      <PageHeader eyebrow="Area 02 · State Licensing Guide" title="State licensing, step by step">
        Get every license, permit and registration your industry needs in your state — with agencies, fees and official links.
      </PageHeader>
      {!allowed ? (
        <UpgradeGate pkg="licensing_guide" title="Unlock the State Licensing Guide">A tailored checklist for your state and industry, so you're properly licensed and registered — a common prerequisite for grant eligibility.</UpgradeGate>
      ) : (
        <>
          <GuideForm />
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Your guides</h2>
          {!items ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : items.length === 0 ? <p className="text-slate-500" data-testid="licensing-empty">No guides yet.</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((g) => {
                const steps = g.guide?.steps || [];
                const pct = steps.length ? Math.round((steps.filter((s) => s.done).length / steps.length) * 100) : 0;
                return (
                  <button key={g.guide_id} onClick={() => navigate(`/dashboard/licensing/${g.guide_id}`)} data-testid={`licensing-guide-card-${g.guide_id}`} className="card-lift text-left bg-white border border-slate-200 rounded-lg p-6">
                    <Landmark className="h-5 w-5 text-emerald-600" />
                    <h3 className="font-display text-lg font-bold text-slate-900 mt-4">{g.industry} · {g.state}</h3>
                    <p className="text-sm text-slate-500">{g.business_type}{g.city ? ` · ${g.city}` : ""}</p>
                    <div className="mt-4">{g.status === "ready" ? <><Progress value={pct} className="h-1.5" /><div className="text-xs text-slate-500 mt-1.5">{pct}% complete</div></> : <span className="text-xs text-slate-400">{g.status === "pending" ? "Building…" : "Failed"}</span>}</div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
